import { useState } from "react";
import { CheckCircle2, XCircle, Eye, RotateCcw, Loader2, X as XIcon, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Phase 5Z.19k — Mass-action toolbar for Zeiterfassung selection.
 *
 * Sticky bottom bar shown when ≥1 entry is checked. Provides one-click
 * bulk Freigabe / Ablehnung / Status-Reset / Geprüft-markieren / Löschen
 * actions via the `/time-entries/bulk-*` backend endpoints.
 *
 * Props:
 *   selectedIds : array of time_entry ids currently checked
 *   onClearSelection() : caller clears its own selection state
 *   onAfterAction() : caller refreshes the list
 *   canApprove : RBAC flag (operations.approve)
 *   canEdit : RBAC flag (operations.edit) — needed for the 'offen' / 'geprueft' flips
 *   canDelete : RBAC flag (operations.delete) — needed for bulk delete
 */
export default function ZeitMassActionToolbar({
  selectedIds = [],
  onClearSelection,
  onAfterAction,
  canApprove,
  canEdit,
  canDelete,
}) {
  const [busy, setBusy] = useState("");
  if (selectedIds.length === 0) return null;
  const count = selectedIds.length;

  async function runApprove() {
    if (!window.confirm(`${count} Einträge freigeben?`)) return;
    setBusy("approve");
    try {
      const { data } = await api.post("/time-entries/bulk-approve", { ids: selectedIds });
      toast.success(`${data.modified} freigegeben`);
      onClearSelection?.();
      onAfterAction?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Massen-Freigabe fehlgeschlagen");
    } finally { setBusy(""); }
  }
  async function runReject() {
    const reason = window.prompt(`${count} Einträge ablehnen?\n\nGrund (optional):`);
    if (reason === null) return;
    setBusy("reject");
    try {
      const { data } = await api.post("/time-entries/bulk-reject", { ids: selectedIds, reason });
      toast.success(`${data.modified} abgelehnt`);
      onClearSelection?.();
      onAfterAction?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Massen-Ablehnung fehlgeschlagen");
    } finally { setBusy(""); }
  }
  async function runSetStatus(status, label, ask = true) {
    if (ask && !window.confirm(`${count} Einträge auf "${label}" setzen?`)) return;
    setBusy(status);
    try {
      const { data } = await api.post("/time-entries/bulk-set-status", { ids: selectedIds, status });
      toast.success(`${data.modified} auf "${label}" gesetzt`);
      onClearSelection?.();
      onAfterAction?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status-Änderung fehlgeschlagen");
    } finally { setBusy(""); }
  }

  async function runDelete() {
    if (!window.confirm(
      `${count} Einträge unwiderruflich löschen?\n\nDiese Aktion kann nicht rückgängig gemacht werden.`
    )) return;
    setBusy("delete");
    try {
      const { data } = await api.post("/time-entries/bulk-delete", { ids: selectedIds });
      toast.success(`${data.deleted} gelöscht`);
      onClearSelection?.();
      onAfterAction?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Massen-Löschung fehlgeschlagen");
    } finally { setBusy(""); }
  }

  return (
    <div
      data-testid="zeit-mass-toolbar"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-zinc-900/95 border border-zinc-700 rounded-lg px-3 py-2.5 shadow-2xl backdrop-blur"
      role="toolbar"
      aria-label="Massen-Aktionen für Zeiteinträge"
    >
      <div className="flex items-center gap-2 pr-3 border-r border-zinc-700">
        <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded bg-amber-500 text-zinc-950 text-sm font-bold tabular-nums"
              data-testid="zeit-mass-count">
          {count}
        </span>
        <span className="text-xs text-zinc-300">ausgewählt</span>
      </div>

      {canApprove && (
        <ToolbarBtn
          onClick={runApprove}
          disabled={!!busy}
          busy={busy === "approve"}
          icon={CheckCircle2}
          label="Freigeben"
          color="emerald"
          testid="zeit-mass-approve"
        />
      )}
      {canApprove && (
        <ToolbarBtn
          onClick={runReject}
          disabled={!!busy}
          busy={busy === "reject"}
          icon={XCircle}
          label="Ablehnen"
          color="rose"
          testid="zeit-mass-reject"
        />
      )}
      {canEdit && (
        <ToolbarBtn
          onClick={() => runSetStatus("geprueft", "Geprüft")}
          disabled={!!busy}
          busy={busy === "geprueft"}
          icon={Eye}
          label="Geprüft"
          color="amber"
          testid="zeit-mass-pruefen"
        />
      )}
      {canEdit && (
        <ToolbarBtn
          onClick={() => runSetStatus("offen", "Offen")}
          disabled={!!busy}
          busy={busy === "offen"}
          icon={RotateCcw}
          label="Offen"
          color="zinc"
          testid="zeit-mass-offen"
        />
      )}
      {canDelete && (
        <ToolbarBtn
          onClick={runDelete}
          disabled={!!busy}
          busy={busy === "delete"}
          icon={Trash2}
          label="Löschen"
          color="red"
          testid="zeit-mass-delete"
        />
      )}

      <button
        type="button"
        onClick={onClearSelection}
        disabled={!!busy}
        data-testid="zeit-mass-clear"
        title="Auswahl aufheben"
        className="ml-2 p-1.5 text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800 rounded transition-colors disabled:opacity-40"
      >
        <XIcon className="w-4 h-4" />
      </button>
    </div>
  );
}

function ToolbarBtn({ onClick, disabled, busy, icon: Icon, label, color, testid }) {
  const colorMap = {
    emerald: "bg-emerald-600 hover:bg-emerald-500 text-white",
    rose: "bg-rose-600 hover:bg-rose-500 text-white",
    red: "bg-red-700 hover:bg-red-600 text-white",
    amber: "bg-amber-600 hover:bg-amber-500 text-white",
    zinc: "bg-zinc-700 hover:bg-zinc-600 text-zinc-100",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${colorMap[color] || colorMap.zinc}`}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
      {label}
    </button>
  );
}
